import React, { useState, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Avatar,
  Divider,
  Chip,
  Stack,
  Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import ShieldIcon from '@mui/icons-material/Shield';
import NicknameDrawer from './NicknameDrawer';
import AvatarDrawer from './AvatarDrawer';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ProfilePath } from '../../../API/config';

const USER_VERIFY_ROUTE = '/verify';
const SUPPORT_VERIFY_ROUTE = '/support/kyc';

const ProfileInfo = ({ open, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [nicknameDrawerOpen, setNicknameDrawerOpen] = useState(false);
  const [avatarDrawerOpen, setAvatarDrawerOpen] = useState(false);

  const user = useSelector((state) => state.user?.data || {});
  const mineKyc = useSelector((state) => state.kyc?.mine || null);

  // Normalize KYC status
  const kycStatus = useMemo(() => {
    const raw = (mineKyc?.status || user?.kyc_status || 'unverified');
    return String(raw).toLowerCase(); // 'verified'|'approved'|'pending'|'rejected'|'unverified'
  }, [mineKyc, user]);

  const isVerified = kycStatus === 'verified' || kycStatus === 'approved';
  const isPending  = kycStatus === 'pending';
  const isUnverified = !isVerified && !isPending;

  // Chip shown in the visible (non-commented) "Identity Verification" section
  const statusChip = isVerified ? (
    <Chip
      icon={<CheckCircleOutlineIcon />}
      label="Verified"
      color="success"
      size="small"
      sx={{ fontWeight: 600 }}
    />
  ) : isPending ? (
    <Chip
      icon={<HourglassBottomIcon />}
      label="Under Review"
      color="warning"
      size="small"
      sx={{ fontWeight: 600 }}
    />
  ) : (
    <Chip
      icon={<HighlightOffIcon />}
      label="Unverified"
      variant="outlined"
      size="small"
      sx={{
        fontWeight: 600,
        color: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        '& .MuiChip-icon': { color: theme.palette.text.secondary }
      }}
    />
  );

  const handleGoVerify = () => {
    if (String(user?.role).toLowerCase() === 'support') {
      navigate(SUPPORT_VERIFY_ROUTE);
    } else {
      navigate(USER_VERIFY_ROUTE);
    }
    onClose?.();
  };

  return (
    <>
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: 320,
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            padding: 2,
          },
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Basic Info</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon sx={{ color: theme.palette.text.primary }} />
          </IconButton>
        </Box>

        {/* Profile header block is commented out intentionally */}
        {/* (You asked not to show KYC here) */}

        <Divider sx={{ mb: 1.5 }} />

        {/* Profile Picture (open Avatar drawer) */}
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, cursor: 'pointer' }}
          onClick={() => setAvatarDrawerOpen(true)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={user?.profile_image ? `${ProfilePath}${user.profile_image}` : undefined}
              alt="User Avatar"
              sx={{ width: 40, height: 40 }}
            />
            <Typography variant="body1">Profile Picture</Typography>
          </Box>
          <ChevronRightIcon />
        </Box>
        <Divider />

        {/* Nickname */}
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2, cursor: 'pointer' }}
          onClick={() => setNicknameDrawerOpen(true)}
        >
          <Typography variant="body1">Nickname</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary" noWrap>
              {user?.username}
            </Typography>
            <ChevronRightIcon />
          </Box>
        </Box>
        <Divider />

        {/* Email */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
          <Typography variant="body1">Email</Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {user?.email}
          </Typography>
        </Box>
        <Divider />

        {/* Password */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
          <Typography variant="body1">Password</Typography>
          <Typography variant="body2" color="text.secondary">********</Typography>
        </Box>
        <Divider />

        {/* Identity Verification (Binance-like) */}
        <Box
          onClick={handleGoVerify}
          sx={{
            mt: 1,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            '&:hover': { bgcolor: theme.palette.action.hover, borderRadius: 1 }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ShieldIcon
              sx={{
                color: isVerified
                  ? 'success.main'
                  : isPending
                  ? 'warning.main'
                  : 'text.secondary'
              }}
            />
            <Box>
              <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Identity Verification
                {statusChip}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isVerified
                  ? 'Your account is verified. Enjoy full access to features.'
                  : isPending
                  ? 'Your verification is under review (up to 24h).'
                  : 'Verify your identity to unlock higher withdrawal limits.'}
              </Typography>
            </Box>
          </Box>
          <ChevronRightIcon />
        </Box>

        <Divider />

        {/* Log Out Button */}
        <Box sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            fullWidth
            sx={{
              color: theme.palette.brand?.main || theme.palette.warning.main,
              borderColor: theme.palette.brand?.main || theme.palette.warning.main,
              '&:hover': {
                bgcolor: (theme.palette.brand?.main && 'rgba(243, 186, 47, 0.1)') || 'rgba(253, 216, 53, 0.08)',
                borderColor: theme.palette.brand?.main || theme.palette.warning.main
              },
              textTransform: 'none',
              fontWeight: 600
            }}
            onClick={() => console.log('Log Out clicked')}
          >
            Log Out
          </Button>
        </Box>
      </Drawer>

      {/* Nickname Drawer */}
      <NicknameDrawer open={nicknameDrawerOpen} onClose={() => setNicknameDrawerOpen(false)} />

      {/* Avatar Drawer */}
      <AvatarDrawer open={avatarDrawerOpen} onClose={() => setAvatarDrawerOpen(false)} />
    </>
  );
};

export default ProfileInfo;
